<section class="hero relative isolate flex items-center overflow-hidden">
  <div class="pointer-events-none absolute inset-0" aria-hidden="true">
    <span class="hero__orb hero__orb--top"></span>
    <span class="hero__orb hero__orb--bottom"></span>
  </div>

  <div
    class="container relative z-10 mx-auto grid items-center gap-10 px-4 pb-20 pt-16 lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)] lg:gap-12 lg:pb-28 lg:pt-20"
  >
    <div class="max-w-2xl space-y-7">
      <h1 class="text-4xl font-bold leading-[1.08] tracking-tight text-on-surface md:text-6xl">
        Turn a question into
        <span class="hero__shimmer block sm:inline">a $10 million idea</span>
      </h1>

      <p class="max-w-xl text-lg font-light leading-relaxed text-neutral-40 md:text-xl">
        Basilon helps teams explore complex questions, connect what matters, and develop stronger
        opportunities from evidence, context and ideas.
      </p>

      <div class="pt-2">
        <a
          matButton="filled"
          class="hero__cta btn-lg group"
          [routerLink]="authFacade.isAuthenticated() ? '/projects' : unauthenticatedRoute"
        >
          {{ authFacade.isAuthenticated() ? 'Open your projects' : 'Request early access' }}
          <mat-icon iconPositionEnd class="transition-transform group-hover:translate-x-1">
            arrow_forward
          </mat-icon>
        </a>
      </div>
    </div>

    <app-hero-map />
  </div>

  <div
    class="hero-wave pointer-events-none absolute inset-x-0 bottom-0 translate-y-px"
    aria-hidden="true"
  >
    <svg class="block h-14 w-full md:h-24" viewBox="0 0 1200 120" preserveAspectRatio="none">
      <path
        class="hero-wave__soft"
        d="M0,44 C200,96 340,8 540,26 C720,42 840,104 1020,78 C1100,66 1155,44 1200,32 L1200,121 L0,121 Z"
      ></path>
      <path
        class="hero-wave__surface"
        d="M0,64 C180,110 320,26 520,44 C700,60 820,116 1000,92 C1090,80 1150,62 1200,50 L1200,121 L0,121 Z"
      ></path>
    </svg>
  </div>
</section>

<section
  id="how-it-works"
  class="landing-section relative scroll-mt-24 overflow-hidden bg-white px-4 pt-24 pb-32"
  aria-labelledby="workflow-title"
>
  <div class="landing-map-grid pointer-events-none absolute inset-0" aria-hidden="true"></div>

  <div class="container relative mx-auto max-w-6xl">
    <div class="grid gap-16 lg:grid-cols-2">
      <div class="space-y-6 lg:sticky lg:top-28 lg:self-start">
        <p class="landing-eyebrow landing-eyebrow--neutral">
          <span aria-hidden="true"></span>
          The Basilon workflow
        </p>
        <h2 id="workflow-title" class="landing-heading">Innovation is a process, not a prompt</h2>

        <ol class="grid gap-x-8 gap-y-7 pt-4 sm:grid-cols-2">
          @for (stage of stages; track stage.title; let index = $index) {
            <li>
              <div class="mb-3 flex items-center gap-3">
                <span
                  class="flex h-10 w-10 items-center justify-center rounded-full border border-outline-variant bg-primary-95"
                >
                  <mat-icon class="icon-sm text-primary-40">{{ stage.icon }}</mat-icon>
                </span>
                <span class="font-mono text-xs hidden lg:block text-neutral-40"
                  >0{{ index + 1 }}</span
                >
              </div>
              <h3 class="mb-1 text-lg font-semibold">{{ stage.title }}</h3>
              <p class="text-sm leading-relaxed text-neutral-40">{{ stage.description }}</p>
            </li>
          }
        </ol>

        <aside
          class="landing-loop-card flex flex-col items-center gap-4 rounded-2xl p-5 sm:flex-row sm:gap-6"
          aria-label="The workflow is iterative"
        >
          <svg
            class="landing-loop-diagram h-36 w-36 shrink-0"
            viewBox="0 0 180 180"
            role="img"
            aria-labelledby="workflow-loop-title"
          >
            <title id="workflow-loop-title">
              Define, explore, develop and prioritize form a continuous loop
            </title>
            <circle class="landing-loop-track" cx="90" cy="90" r="58"></circle>
            <circle class="landing-loop-progress" cx="90" cy="90" r="58"></circle>

            <g>
              <circle class="landing-loop-halo" cx="90" cy="32" r="10"></circle>
              <circle class="landing-loop-node" cx="90" cy="32" r="5"></circle>
              <text x="90" y="52" text-anchor="middle">{{ stages[0].title }}</text>
            </g>
            <g>
              <circle class="landing-loop-halo" cx="148" cy="90" r="10"></circle>
              <circle class="landing-loop-node" cx="148" cy="90" r="5"></circle>
              <text x="134" y="93" text-anchor="end">{{ stages[1].title }}</text>
            </g>
            <g>
              <circle class="landing-loop-halo" cx="90" cy="148" r="10"></circle>
              <circle class="landing-loop-node" cx="90" cy="148" r="5"></circle>
              <text x="90" y="136" text-anchor="middle">{{ stages[2].title }}</text>
            </g>
            <g>
              <circle class="landing-loop-halo" cx="32" cy="90" r="10"></circle>
              <circle class="landing-loop-node" cx="32" cy="90" r="5"></circle>
              <text x="46" y="93" text-anchor="start">{{ stages[3].title }}</text>
            </g>
          </svg>

          <div class="space-y-2">
            <p
              class="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary"
            >
              <span class="relative flex h-1.5 w-1.5" aria-hidden="true">
                <span
                  class="landing-status-pulse absolute inline-flex h-full w-full rounded-full bg-primary opacity-70"
                ></span>
                <span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary"></span>
              </span>
              Always running
            </p>
            <p class="text-sm leading-relaxed text-neutral-40">
              Every pass adds new evidence and connections to your project, so the next round starts
              from everything you already know instead of a blank prompt.
            </p>
          </div>
        </aside>
      </div>

      <article class="landing-card p-6">
        <div class="rounded-xl border bg-surface p-4">
          <p class="mb-2 text-xs uppercase tracking-wider text-neutral-40">Starting question</p>
          <p class="font-medium leading-snug">{{ workflowQuestion }}</p>
        </div>

        <div class="flex justify-center py-3 text-neutral-40" aria-hidden="true">
          <mat-icon>arrow_downward</mat-icon>
        </div>

        <div class="grid gap-3 sm:grid-cols-3">
          @for (column of workflowColumns; track column.label) {
            <div class="rounded-xl border border-outline-variant p-4">
              <p class="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-40">
                {{ column.label }}
              </p>
              <ul class="space-y-2">
                @for (item of column.items; track item) {
                  <li class="flex items-start gap-2 text-sm leading-snug">
                    <span
                      class="landing-dot mt-1.5"
                      [class]="column.tone"
                      aria-hidden="true"
                    ></span>
                    {{ item }}
                  </li>
                }
              </ul>
            </div>
          }
        </div>

        <div class="mt-4 overflow-hidden rounded-xl border border-outline-variant bg-surface/60">
          <div
            class="flex flex-wrap items-center justify-between gap-2 border-b border-outline-variant px-3 py-2"
          >
            <p
              class="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary"
            >
              <span class="relative flex h-1.5 w-1.5" aria-hidden="true">
                <span
                  class="landing-status-pulse absolute inline-flex h-full w-full rounded-full bg-primary opacity-70"
                ></span>
                <span class="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary"></span>
              </span>
              What connects to what
            </p>
            <p class="text-[10px] text-neutral-40">Reviewed across 128 findings</p>
          </div>

          <ul class="divide-y divide-outline-variant/60">
            @for (connection of workflowConnections; track connection.source) {
              <li class="flex items-center gap-2 px-3 py-2 text-[11px]">
                <span>
                  {{ connection.source }}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-arrow-right h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  data-lov-id="src/components/MapMotifs.tsx:123:6"
                  data-lov-name="ArrowRight"
                  data-component-path="src/components/MapMotifs.tsx"
                  data-component-line="123"
                  data-component-file="MapMotifs.tsx"
                  data-component-name="ArrowRight"
                  data-component-content="%7B%7D"
                >
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
                <span class="text-center rounded-full border border-neutral-60 px-2">
                  {{ connection.relationship }}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-arrow-right h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  data-lov-id="src/components/MapMotifs.tsx:123:6"
                  data-lov-name="ArrowRight"
                  data-component-path="src/components/MapMotifs.tsx"
                  data-component-line="123"
                  data-component-file="MapMotifs.tsx"
                  data-component-name="ArrowRight"
                  data-component-content="%7B%7D"
                >
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
                <span>
                  {{ connection.target }}
                </span>
              </li>
            }
          </ul>
        </div>

        <div class="flex justify-center py-3 text-neutral-40" aria-hidden="true">
          <mat-icon>arrow_downward</mat-icon>
        </div>

        <div class="rounded-xl border border-neutral-87 bg-neutral-95 p-4">
          <p class="mb-1 text-xs uppercase tracking-wider text-neutral-40">
            Prioritized opportunities
          </p>
          <p class="font-medium">High-potential directions for lower-energy data centers</p>
        </div>
      </article>
    </div>
  </div>

  <div class="landing-wave" aria-hidden="true">
    <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
      <path
        d="M0,44 C200,96 340,8 540,26 C720,42 840,104 1020,78 C1100,66 1155,44 1200,32 L1200,121 L0,121 Z"
        fill="var(--color-home-section)"
        opacity="0.45"
      ></path>
      <path
        d="M0,64 C180,110 320,26 520,44 C700,60 820,116 1000,92 C1090,80 1150,62 1200,50 L1200,121 L0,121 Z"
        fill="var(--color-home-section)"
      ></path>
    </svg>
  </div>
</section>
